// src/utils/exportToPdf.js - CORRECTED
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const exportToPdf = async (elementId, fileName = 'student-report.pdf') => {
    const reportContentElement = document.getElementById(elementId);
    if (!reportContentElement) {
        console.error('Element not found for PDF export:', elementId);
        alert('Error: Report content not found for PDF generation.');
        return;
    }

    // Create loading indicator
    const loadingIndicator = document.createElement('div');
    loadingIndicator.style.position = 'fixed';
    loadingIndicator.style.top = '50%';
    loadingIndicator.style.left = '50%';
    loadingIndicator.style.transform = 'translate(-50%, -50%)';
    loadingIndicator.style.padding = '20px 30px';
    loadingIndicator.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    loadingIndicator.style.color = 'white';
    loadingIndicator.style.borderRadius = '10px';
    loadingIndicator.style.fontWeight = 'bold';
    loadingIndicator.style.zIndex = '9999';
    loadingIndicator.innerText = 'Preparing PDF Export...';
    document.body.appendChild(loadingIndicator);

    // Initialize PDF with A4 dimensions
    const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
    });

    // A4 dimensions
    const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
    const pdfHeight = pdf.internal.pageSize.getHeight(); // 297mm

    const reportPages = reportContentElement.getElementsByClassName('report-page');
    if (reportPages.length === 0) {
        console.error('No elements with class "report-page" found within:', elementId);
        document.body.removeChild(loadingIndicator);
        alert('Error: No report pages found for PDF generation.');
        return;
    }

    try {
        // Pre-process all student images to ensure they load properly
        await preProcessImages(reportPages);

        for (let i = 0; i < reportPages.length; i++) {
            // Update loading indicator
            loadingIndicator.innerText = `Generating PDF... Page ${i + 1} of ${reportPages.length}`;

            const pageElement = reportPages[i];

            // Hide elements that shouldn't appear in PDF
            const hiddenElements = pageElement.querySelectorAll('.no-print');
            hiddenElements.forEach(el => {
                el.setAttribute('data-original-display', el.style.display);
                el.style.display = 'none';
            });

            // Force a brief delay to ensure rendering
            await new Promise(resolve => setTimeout(resolve, 300));

            try {
                // Create a clone of the page for modification without affecting the original
                const pageClone = pageElement.cloneNode(true);
                const tempContainer = document.createElement('div');
                tempContainer.style.position = 'absolute';
                tempContainer.style.left = '-9999px';
                tempContainer.style.top = '-9999px';
                tempContainer.style.width = '210mm';
                tempContainer.style.height = '297mm';
                tempContainer.appendChild(pageClone);
                document.body.appendChild(tempContainer);

                // Apply enhancements to the clone
                enhancePageForPdf(pageClone);

                // Capture the enhanced page
                const canvas = await html2canvas(pageClone, {
                    scale: 3, // Higher scale for better quality
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                    width: 210 * 3.779528, // A4 width in px (96 DPI)
                    height: 297 * 3.779528, // A4 height in px (96 DPI)
                    backgroundColor: '#ffffff',
                    imageTimeout: 30000,
                });

                // Remove the temporary clone
                document.body.removeChild(tempContainer);

                // Convert canvas to image
                const imgData = canvas.toDataURL('image/jpeg', 1.0);

                // Add a new page for pages after the first
                if (i > 0) {
                    pdf.addPage();
                }

                // Add the image to fill the PDF page
                pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');

                // Restore hidden elements
                hiddenElements.forEach(el => {
                    el.style.display = el.getAttribute('data-original-display') || '';
                    el.removeAttribute('data-original-display');
                });

            } catch (error) {
                console.error(`Error capturing page ${i + 1} for PDF:`, error);
                document.body.removeChild(loadingIndicator);
                alert(`Error generating PDF on page ${i + 1}. Please try again.`);
                return;
            }
        }

        // Update loading indicator
        loadingIndicator.innerText = 'Finalizing PDF...';

        // Short delay for UI feedback
        await new Promise(resolve => setTimeout(resolve, 500));

        // Save the PDF
        pdf.save(fileName);

    } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Error generating PDF. Please try again.');
    } finally {
        // Remove loading indicator
        document.body.removeChild(loadingIndicator);
    }
};

// Function to enhance a page clone for PDF output
function enhancePageForPdf(pageClone) {
    // Ensure base styling
    pageClone.style.width = '210mm';
    pageClone.style.height = '297mm';
    pageClone.style.padding = '15mm 20mm'; // Match the reference image
    pageClone.style.margin = '0';
    pageClone.style.boxSizing = 'border-box';
    pageClone.style.position = 'relative';
    pageClone.style.backgroundColor = 'white';
    pageClone.style.color = '#333333';
    pageClone.style.fontSize = '10pt';
    pageClone.style.lineHeight = '1.4';
    pageClone.style.fontFamily = 'Arial, sans-serif';

    // Fix titles
    const partA1Title = pageClone.querySelector('h2');
    if (partA1Title && partA1Title.textContent.includes('PART-A (1)')) {
        partA1Title.style.color = '#F97316';
        partA1Title.style.fontSize = '18pt';
        partA1Title.style.fontWeight = 'bold';
        partA1Title.style.textAlign = 'center';
        partA1Title.style.marginBottom = '24px';
        partA1Title.style.marginTop = '8px';
    }

    // Fix general information header
    const generalInfoHeader = pageClone.querySelector('.bg-orange-600');
    if (generalInfoHeader) {
        generalInfoHeader.style.backgroundColor = '#F97316';
        generalInfoHeader.style.color = 'white';
        generalInfoHeader.style.padding = '8px 16px';
        generalInfoHeader.style.textAlign = 'center';
        generalInfoHeader.style.fontWeight = 'bold';
        generalInfoHeader.style.fontSize = '14pt';
        generalInfoHeader.style.marginLeft = '-20mm';
        generalInfoHeader.style.marginRight = '-20mm';
    }

    // Fix attendance header
    const attendanceHeader = pageClone.querySelector('.bg-orange-500');
    if (attendanceHeader) {
        attendanceHeader.style.backgroundColor = '#F97316';
        attendanceHeader.style.color = 'white';
        attendanceHeader.style.padding = '4px 12px';
        attendanceHeader.style.fontWeight = 'bold';
    }

    // Fix interest section
    const interestSection = pageClone.querySelector('.border.border-orange-300');
    if (interestSection) {
        interestSection.style.backgroundColor = '#FFF7ED';
        interestSection.style.borderColor = '#FB923C';
        interestSection.style.padding = '12px';
    }

    // Fix table styling
    const tables = pageClone.querySelectorAll('table');
    tables.forEach(table => {
        table.style.borderCollapse = 'collapse';
        table.style.width = '100%';

        // Fix table headers
        const headers = table.querySelectorAll('th');
        headers.forEach(header => {
            header.style.backgroundColor = '#FED7AA';
            header.style.color = '#B91C1C';
            header.style.border = '1px solid #1F2937';
            header.style.padding = '4px';
            header.style.textAlign = 'center';
            header.style.fontWeight = 'bold';
            header.style.fontSize = '9pt';
        });

        // Fix table cells
        const cells = table.querySelectorAll('td');
        cells.forEach(cell => {
            cell.style.border = '1px solid #1F2937';
            cell.style.padding = '4px';

            // Special handling for first column cells (containing labels)
            if (cell.textContent.includes('Working Days') ||
                cell.textContent.includes('Days Attended') ||
                cell.textContent.includes('% of Attendance')) {
                cell.style.backgroundColor = '#FED7AA';
                cell.style.color = '#B91C1C';
                cell.style.fontWeight = 'bold';
                cell.style.fontSize = '9pt';
                cell.style.textAlign = 'left';
                cell.style.paddingLeft = '8px';
            }
        });
    });

    // Ensure all checkboxes appear properly
    const checkboxes = pageClone.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
        // Preserve the checked state
        const isChecked = checkbox.checked;

        // Create a visual checkbox that will render properly in PDF
        const visualCheckbox = document.createElement('div');
        visualCheckbox.style.display = 'inline-block';
        visualCheckbox.style.width = '12px';
        visualCheckbox.style.height = '12px';
        visualCheckbox.style.border = '1px solid #333';
        visualCheckbox.style.backgroundColor = isChecked ? '#F97316' : 'white';
        visualCheckbox.style.marginRight = '6px';
        visualCheckbox.style.position = 'relative';

        // Add checkmark if checked
        if (isChecked) {
            const checkmark = document.createElement('div');
            checkmark.style.position = 'absolute';
            checkmark.style.top = '1px';
            checkmark.style.left = '4px';
            checkmark.style.width = '4px';
            checkmark.style.height = '8px';
            checkmark.style.borderRight = '2px solid white';
            checkmark.style.borderBottom = '2px solid white';
            checkmark.style.transform = 'rotate(45deg)';
            visualCheckbox.appendChild(checkmark);
        }

        // Replace the checkbox with our visual version
        if (checkbox.parentNode) {
            checkbox.style.display = 'none';
            checkbox.parentNode.insertBefore(visualCheckbox, checkbox);
        }
    });

    // Process all images
    const images = pageClone.querySelectorAll('img');
    images.forEach(img => {
        img.setAttribute('crossOrigin', 'anonymous');

        // Special handling for the student photo
        if (img.closest('[class*="border-2"]')) {
            const container = img.closest('[class*="border-2"]');
            if (container) {
                container.style.overflow = 'hidden';
                container.style.display = 'flex';
                container.style.alignItems = 'center';
                container.style.justifyContent = 'center';
                container.style.backgroundColor = '#f9fafb';
                container.style.border = '2px solid #4B5563';
            }

            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'cover';
        }
    });

    return pageClone;
}

// Helper function to pre-process images for better PDF rendering
async function preProcessImages(reportPages) {
    const imagePromises = [];

    // Find all images in report pages
    for (let i = 0; i < reportPages.length; i++) {
        const images = reportPages[i].querySelectorAll('img');

        images.forEach(img => {
            // Add crossOrigin attribute to all images
            img.setAttribute('crossOrigin', 'anonymous');

            // Create a promise to ensure the image is loaded
            const promise = new Promise((resolve) => {
                if (img.complete) {
                    resolve();
                    return;
                }

                img.onload = () => resolve();
                img.onerror = () => {
                    console.warn('Failed to load image for PDF export:', img.src);
                    resolve(); // Resolve anyway to continue with PDF generation
                };
            });

            imagePromises.push(promise);
        });
    }

    // Wait for all images to be processed
    if (imagePromises.length > 0) {
        await Promise.all(imagePromises);
    }

    // Give a short delay to ensure everything is rendered
    await new Promise(resolve => setTimeout(resolve, 200));

    return true;
}